import { api } from './client';

/** Carteira tocada por uma transação. Ausente/null = legado (saldo). */
export type WalletKind = 'cash' | 'bonus';

export interface WalletOut {
  /**
   * Carteira de SALDO (dinheiro pago). Pode ser NEGATIVA — pendência de
   * recarga com margem Aliança; nesse estado as recargas ficam bloqueadas
   * até o próximo aporte (mínimo = min_topup_brl).
   */
  balance_brl: string;
  /** LEGADO — acabou a reserva (migration 050); sempre "0.00". */
  reserved_brl: string;
  /** Carteira de CASHBACK — hermética: nunca negativa, nunca vira saldo. */
  bonus_brl: string;
  /** LEGADO — acabou a reserva (migration 050); sempre "0.00". */
  reserved_bonus_brl: string;
  /** Disponível do saldo. Igual a balance_brl desde a 050. Negativo em pendência. */
  available_brl: string;
  bonus_available_brl: string;
  /** Pendência como valor positivo; "0.00" sem dívida. */
  debt_brl: string;
  /** Mínimo pra iniciar recarga (regra do sistema). */
  min_reserve_brl?: string;
  /**
   * Saldo mínimo pra INICIAR uma recarga pagando com SALDO (default R$ 25).
   * NÃO vale pra cashback — lá continua valendo `min_reserve_brl`, porque
   * pagar com cashback não gera dívida.
   */
  min_balance_brl?: string;
  /** Mínimo do próximo aporte — dívida + mínimo pra voltar a operar. */
  min_topup_brl?: string;
}

export interface WalletTransaction {
  id: string;
  type: string;
  amount_brl: string;
  balance_after: string;
  description: string | null;
  session_id: string | null;
  point_name?: string | null;
  energy_kwh?: string | null;
  cashback_rule_version: number | null;
  valid_until: string | null;
  /** Carteira tocada. null = legado (conta como saldo). */
  wallet?: WalletKind | null;
  created_at: string;
}

export interface WalletTransactionsOut {
  items: WalletTransaction[];
  total: number;
}

export const walletApi = {
  get: () => api.get<WalletOut>('/wallet'),
  transactions: (limit = 50, offset = 0, wallet?: WalletKind) =>
    api.get<WalletTransactionsOut>(
      `/wallet/transactions?limit=${limit}&offset=${offset}` +
        (wallet ? `&wallet=${wallet}` : '')
    )
};

/** Helpers numéricos compartilhados pelas telas (fallbacks defensivos). */
export function walletNumbers(w: WalletOut | null) {
  const balance = w ? Number(w.balance_brl) : 0;
  const reserved = w ? Number(w.reserved_brl) : 0;
  const bonus = w ? Number(w.bonus_brl ?? 0) : 0;
  const reservedBonus = w ? Number(w.reserved_bonus_brl ?? 0) : 0;
  const available = w?.available_brl != null ? Number(w.available_brl) : balance - reserved;
  const bonusAvailable =
    w?.bonus_available_brl != null ? Number(w.bonus_available_brl) : bonus - reservedBonus;
  const debt = w?.debt_brl != null ? Number(w.debt_brl) : Math.max(0, -balance);
  // Regras do sistema: mínimo pra iniciar com SALDO e mínimo do próximo aporte
  // (já inclui a dívida). Fallbacks = defaults do backend.
  const minBalance = Number(w?.min_balance_brl ?? 25);
  const minTopup = Number(w?.min_topup_brl ?? 5);
  return {
    balance,
    reserved,
    bonus,
    reservedBonus,
    available,
    bonusAvailable,
    debt,
    minBalance,
    minTopup
  };
}
